<script setup lang="ts">
import { get } from 'lodash-es';
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useApi } from '@/api';
import eventBus from '@/setup/eventBus';
import { typeGuard } from '@/types/core';
import type { EmitsToListeners, ErrorList, FormErrors } from '@/types/core';
import type { CrmAction } from '@/types/crm';

interface ConfirmActionsPayload {
  defaultName: string;
  actions: CrmAction[];
}
interface ValidationErrorResponse {
  data?: {
    code?: string;
    errors?: FormErrors;
  };
}

interface Props {
  currentBrand?: string;
  actions?: ConfirmActionsPayload | null;
  selectedActions?: boolean[] | null;
}

interface Emits {
  close: [];
}

export interface ConfirmSendTestEmailParams {
  data?: Props;
  listeners?: EmitsToListeners<Emits>;
}

const props = withDefaults(defineProps<Props>(), {
  currentBrand: '',
  actions: null,
  selectedActions: null,
});

const emit = defineEmits<Emits>();
const api = useApi();
const store = useStore();
const { t } = useI18n();

const isVisible = ref(false);
const isLoading = ref(false);
const confirmSendTestSelectedActions = ref<boolean[]>([]);
const confirmSendTestActions = ref<ConfirmActionsPayload | null>(null);
const emails = ref('');
const errors = ref<FormErrors>({});

const adminInfo = computed(() => store.getters['auth/adminInfo'] || {});
const languageIndex = computed<Record<string, { name?: string }>>(() => {
  const languages = store.state.misc?.languages || [];
  return languages.reduce((acc: Record<string, { name?: string }>, item: { code: string; name?: string }) => {
    acc[item.code] = item;
    return acc;
  }, {});
});
const isConfirmSendTestActionsDisabled = computed(() => (
  confirmSendTestActions.value?.actions.map((action) => !String(action.message || '').trim()) || []
));
const isConfirmSendTestSelectedAll = computed<boolean>({
  get: () => {
    if (!confirmSendTestActions.value) return false;
    const filtered = confirmSendTestSelectedActions.value
      .filter((item, index) => !isConfirmSendTestActionsDisabled.value[index]);
    return filtered.length > 0 && filtered.every((item) => item);
  },
  set: (value) => {
    if (!confirmSendTestActions.value) return;
    confirmSendTestSelectedActions.value = confirmSendTestActions.value.actions
      .map((_, index) => (isConfirmSendTestActionsDisabled.value[index] ? false : value));
  },
});
const isConfirmSendTestDisabled = computed(() => confirmSendTestSelectedActions.value.every((item) => !item));
const isConfirmSendTestSelectedAllDisabled = computed(() => (
  isConfirmSendTestActionsDisabled.value.every((item) => !!item)
));

function getErrorMessage(error: ErrorList | ErrorList[] | null | undefined): string {
  if (Array.isArray(error)) {
    return getErrorMessage(error.filter((err) => !!err).find((err) => getErrorMessage(err)));
  }

  return error?.message || '';
}
const emailsError = computed(() => getErrorMessage(get(errors.value, 'emails', [])));

function open() {
  confirmSendTestSelectedActions.value = [...(props.selectedActions || [])];
  confirmSendTestActions.value = props.actions;
  errors.value = {};
  emails.value = '';
  isLoading.value = false;
  isVisible.value = true;
}

function close() {
  isVisible.value = false;
  emit('close');
}

async function handleConfirmSendTest() {
  if (isLoading.value || !confirmSendTestActions.value) return;
  isLoading.value = true;

  const actions = confirmSendTestActions.value.actions
    .filter((item, index) => confirmSendTestSelectedActions.value[index]);
  const parsedEmails = emails.value.split('\n').map((email) => email.trim()).filter((email) => !!email);
  const payload = {
    site_id: props.currentBrand,
    emails: parsedEmails,
    actions: actions.map((item) => ({
      email_subject: item.email_subject,
      message: item.message,
      email_template_id: item.email_template_id,
    })),
  };

  try {
    await api.sendActivityTestEmails(payload);
    close();
    eventBus.$emit('notify-event', {
      type: 'info',
      text: t('crm.activities.actions.send_test_email.success'),
    });
  } catch (error: unknown) {
    if (typeGuard<ValidationErrorResponse>(error, (typedError) => typedError?.data?.code === 'VALIDATION_ERROR')) {
      errors.value = error.data?.errors || {};
    }
  } finally {
    isLoading.value = false;
  }
}

defineExpose({
  open,
  close,
});
</script>

<template>
  <Popup
    v-model:is-visible="isVisible"
    :title="$t('crm.activities.actions.send_test_email.title')"
    :width="480"
    :is-loading="isLoading"
    @close="close()"
  >
    <template v-slot:body>
      <div class="t:space-y-16">
        <div class="t:flex t:gap-12">
          <UiIcon
            name="exclamation-triangle"
            :size="24"
            color="var(--text-positive)"
          />
          <UiLabel
            v-if="confirmSendTestActions"
            :label="$t('crm.activities.actions.send_test_email.msg', {
              mailName: confirmSendTestActions.defaultName
                ? confirmSendTestActions.defaultName
                : $t('crm.activities.actions.empty_name'),
              email: adminInfo.email,
            })"
          />
        </div>
        <UiTextarea
          v-model="emails"
          :label="$t('crm.activities.actions.send_test_email.emails_title')"
          :placeholder="$t('crm.activities.actions.send_test_email.emails_placeholder')"
          :rows="6"
          :error="emailsError"
        />
        <div
          v-if="confirmSendTestActions"
          class="t:space-y-8"
        >
          <UiCheckbox
            v-model="isConfirmSendTestSelectedAll"
            :label="$t('crm.activities.actions.send_test_email.all_localizations')"
            :disabled="isConfirmSendTestSelectedAllDisabled"
          />
          <div
            v-for="(actionGroup, index) in confirmSendTestActions!.actions"
            :key="index"
            class="t:flex t:items-center t:gap-4"
          >
            <UiCheckbox
              v-model="confirmSendTestSelectedActions[index]"
              :label="languageIndex[actionGroup.language_code]?.name || actionGroup.language_code"
              :disabled="isConfirmSendTestActionsDisabled[index]"
            />
            <ActionIcon
              v-if="isConfirmSendTestActionsDisabled[index]"
              icon="exclamation-triangle"
              color="var(--text-negative)"
              :is-overlay="false"
              :tooltip="$t('crm.tooltips.empty_mail_message')"
            />
          </div>
        </div>
      </div>
    </template>
    <template v-slot:footer>
      <div class="t:flex t:justify-end t:space-x-8">
        <UiButton
          type="negative"
          icon="times"
          :disabled="isLoading"
          @click="close()"
        >
          {{ $t('crm.buttons.cancel') }}
        </UiButton>
        <UiButton
          icon="check"
          :disabled="isConfirmSendTestDisabled || isLoading"
          filled
          @click="handleConfirmSendTest"
        >
          {{ $t('crm.buttons.confirm') }}
        </UiButton>
      </div>
    </template>
  </Popup>
</template>
